/*
 * Purpose: Audit plan requirements — secrets, OAuth, inbox availability
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
import type { OutcomePlan } from './types';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { getGmailAccessToken, hasGoogleOAuthConfig } from '@/services/connectors/gmailOAuth';
import {
  getLinkedInAccessToken,
  hasLinkedInOAuthConfig,
  isLinkedInConnected,
} from '@/services/connectors/linkedinOAuth';
import { checkWebhookServerHealth } from '@/services/connectors/gmailInbound';
import { resolveConnectorsForPlan } from '@/services/connectors/library/connectorResolver';
import type { ConnectorFactoryResult } from '@/services/connectors/library/connectorFactory';

export type ReadinessKind = 'secret' | 'oauth' | 'inbox' | 'runtime';

export type ReadinessStatus = 'ready' | 'missing' | 'optional';

export interface ReadinessRequirement {
  id: string;
  label: string;
  description: string;
  status: ReadinessStatus;
  kind: ReadinessKind;
  secretName?: string;
  connectorId?: string;
  required: boolean;
}

export interface PlanReadinessReport {
  items: ReadinessRequirement[];
  requiredReady: number;
  requiredTotal: number;
  allRequiredReady: boolean;
}

function hasSecret(name: string): boolean {
  return hasResolvedSecret(name);
}

function gmailSatisfied(plan: OutcomePlan): boolean {
  if (getGmailAccessToken()) return true;
  if (plan.gmailInboxId) return true;
  return false;
}

function needsGmailConnector(plan: OutcomePlan): boolean {
  if (plan.useGmailInbound) return true;
  return (
    plan.resolvedConnectors?.some((c) => c.connectorId === 'ab.gmail') ||
    plan.steps.some((s) => s.subtype === 'gmail_inbound') ||
    plan.connectors.some((c) => c.id.includes('gmail'))
  );
}

function needsOpenAi(plan: OutcomePlan): boolean {
  return (
    plan.resolvedConnectors?.some((c) => c.connectorId === 'ab.openai') ||
    plan.steps.some((s) => s.config?.url?.toString().includes('openai') || s.label.toLowerCase().includes('openai')) ||
    plan.connectors.some((c) => c.id.includes('openai'))
  );
}

function needsLinkedInConnector(plan: OutcomePlan): boolean {
  const text = `${plan.rawOutcome} ${plan.interpretedGoal}`.toLowerCase();
  return (
    plan.matchedTemplateId === 'linkedin_job_hunt_pipeline' ||
    plan.resolvedConnectors?.some((c) => c.connectorId === 'ab.linkedin') ||
    plan.connectors.some((c) => c.id.includes('linkedin')) ||
    /\blinkedin\b/.test(text)
  );
}

export function auditPlanReadiness(plan: OutcomePlan): PlanReadinessReport {
  const items: ReadinessRequirement[] = [];

  if (needsOpenAi(plan)) {
    const ready = hasSecret('OPENAI_API_KEY');
    items.push({
      id: 'openai_api_key',
      label: 'OpenAI API key',
      description: 'Required for AI tailoring, scoring, and cover-letter steps.',
      status: ready ? 'ready' : 'missing',
      kind: 'secret',
      secretName: 'OPENAI_API_KEY',
      connectorId: 'ab.openai',
      required: true,
    });
  }

  if (needsGmailConnector(plan)) {
    const gmailReady = gmailSatisfied(plan);
    items.push({
      id: 'gmail_access',
      label: 'Gmail connection',
      description: gmailReady
        ? getGmailAccessToken()
          ? 'Gmail API connected via OAuth.'
          : `Edge inbox ready (${plan.gmailInboxId}).`
        : 'Connect Gmail OAuth or create an edge inbox below.',
      status: gmailReady ? 'ready' : 'missing',
      kind: getGmailAccessToken() ? 'oauth' : plan.gmailInboxId ? 'inbox' : 'oauth',
      connectorId: 'ab.gmail',
      required: true,
    });

    if (!gmailReady && !hasGoogleOAuthConfig()) {
      items.push({
        id: 'google_client_id',
        label: 'Google OAuth client ID',
        description: 'Needed for Connect Gmail — create a Web client in Google Cloud Console.',
        status: 'missing',
        kind: 'secret',
        secretName: 'GOOGLE_CLIENT_ID',
        connectorId: 'ab.gmail',
        required: true,
      });
    }
  }

  if (needsLinkedInConnector(plan)) {
    const linkedInReady = isLinkedInConnected();
    items.push({
      id: 'linkedin_oauth',
      label: 'LinkedIn connection',
      description: linkedInReady
        ? 'LinkedIn OIDC token saved — profile available for tailoring.'
        : 'Save app credentials and Connect LinkedIn (Sign In with LinkedIn product).',
      status: linkedInReady ? 'ready' : 'missing',
      kind: 'oauth',
      connectorId: 'ab.linkedin',
      required: false,
    });

    if (!linkedInReady && !hasLinkedInOAuthConfig()) {
      if (!hasSecret('LINKEDIN_CLIENT_ID')) {
        items.push({
          id: 'linkedin_client_id',
          label: 'LinkedIn Client ID',
          description: 'From your LinkedIn developer app Auth tab.',
          status: 'missing',
          kind: 'secret',
          secretName: 'LINKEDIN_CLIENT_ID',
          connectorId: 'ab.linkedin',
          required: false,
        });
      }
      if (!hasSecret('LINKEDIN_CLIENT_SECRET')) {
        items.push({
          id: 'linkedin_client_secret',
          label: 'LinkedIn Client Secret',
          description: 'Required for OAuth token exchange (stored locally in Secrets).',
          status: 'missing',
          kind: 'secret',
          secretName: 'LINKEDIN_CLIENT_SECRET',
          connectorId: 'ab.linkedin',
          required: false,
        });
      }
    }
  }

  for (const c of plan.connectors) {
    if (c.status !== 'needs_credentials' || c.id.includes('gmail') || c.id.includes('openai')) continue;
    const secretGuess =
      c.id.includes('linkedin')
        ? 'LINKEDIN_TOKEN'
        : c.authType === 'oauth2'
          ? `${c.id.toUpperCase().replace(/-/g, '_')}_TOKEN`
          : `${c.id.toUpperCase().replace(/-/g, '_')}_KEY`;
    const ready = c.id.includes('linkedin') ? Boolean(getLinkedInAccessToken()) : hasSecret(secretGuess);
    items.push({
      id: `legacy_${c.id}`,
      label: c.name,
      description: c.purpose,
      status: ready ? 'ready' : 'missing',
      kind: c.authType === 'oauth2' ? 'oauth' : 'secret',
      secretName: c.authType !== 'oauth2' ? secretGuess : undefined,
      required: false,
    });
  }

  const required = items.filter((i) => i.required);
  const requiredReady = required.filter((i) => i.status === 'ready').length;

  return {
    items,
    requiredReady,
    requiredTotal: required.length,
    allRequiredReady: required.length === 0 || requiredReady === required.length,
  };
}

export function rehydratePlanConnectors(plan: OutcomePlan): OutcomePlan {
  const factory: ConnectorFactoryResult = {
    connectorIds: plan.resolvedConnectors?.map((c) => c.connectorId) ?? [],
    builtConnectors: plan.builtConnectors ?? [],
    rationale: plan.connectorFactoryNote,
  };
  return {
    ...plan,
    resolvedConnectors: resolveConnectorsForPlan(plan, factory),
  };
}

export async function checkInboundRuntime(): Promise<boolean> {
  return checkWebhookServerHealth();
}
